import * as React from 'react'

import { useDispatch } from 'react-redux'
import { Item, sortOrder } from '../../types'
import { FormattedMessage } from 'react-intl'
import { TableHeader, FittedTable } from '../styled'
import { toggleCreateItemModal, openEditItemModal, Query } from './types'
import styled from 'styled-components'
import { EditFilled, PlusOutlined } from '@ant-design/icons'

import { OptionalAmount } from '../Amount'
import ButtonGroup from '../ButtonGroup'
import ConfirmDeleteModal from '../ConfirmDeleteModal'
import ContributorButton from '../ContributorButton'
import { useOnChangeHandler } from '../../redux'
import { flow } from 'fp-ts/lib/function'
import { useDeleteItem, useItems } from './queries'

const FlexedEnd = styled.div`
  display: flex;
  justify-content: flex-end;
`

const Items = () => {
  const [{ page, itemsPerPage, sort }, setQuery] = React.useState<
    Partial<Query>
  >({
    page: 0,
    itemsPerPage: 10,
  })
  const itemsQuery = useItems({
    page: page ?? 0,
    itemsPerPage: itemsPerPage ?? 10,
    sort,
  })
  const deleteItem = useDeleteItem()

  const data = itemsQuery.data?.data ?? []
  const count = itemsQuery.data?.count ?? 0
  const loading = itemsQuery.isLoading
  const dispatch = useDispatch()
  const onChangeHandler = useOnChangeHandler<Item>(setQuery)
  const [hoveredID, setHoveredID] = React.useState<number | undefined>(
    undefined
  )

  return (
    <FittedTable<Item & { key: string }>
      size="middle"
      title={() => (
        <TableHeader title={<FormattedMessage id="items" />}>
          <FlexedEnd>
            <ContributorButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={flow(toggleCreateItemModal, dispatch)}
            >
              <FormattedMessage id="addItem" />
            </ContributorButton>
          </FlexedEnd>
        </TableHeader>
      )}
      dataSource={data.map(i => ({ ...i, key: `${i.id}` }))}
      onRow={i => ({
        onMouseEnter: () => setHoveredID(i.id),
        onMouseLeave: () =>
          i.id === hoveredID ? setHoveredID(undefined) : undefined,
      })}
      loading={loading}
      pagination={{
        pageSize: itemsPerPage,
        current: (page ?? 0) + 1,
        total: count,
      }}
      columns={[
        {
          sorter: true,
          sortOrder: sortOrder('name', sort),
          title: <FormattedMessage id="name" />,
          dataIndex: 'name',
          key: 'name',
        },
        {
          sorter: true,
          sortOrder: sortOrder('description', sort),
          title: <FormattedMessage id="description" />,
          dataIndex: 'description',
          key: 'description',
        },
        {
          sorter: true,
          sortOrder: sortOrder('price', sort),
          title: <FormattedMessage id="rate" />,
          dataIndex: 'price',
          key: 'price',
          align: 'right',
          render: (_, i) => <OptionalAmount amount={i.price} />,
        },
        {
          sorter: true,
          sortOrder: sortOrder('price', sort),
          title: <FormattedMessage id="rateType" />,
          dataIndex: 'rateType',
          key: 'rateType',
          // TODO: read rateType from item
          render: () => 'some item',
        },
        {
          title: <FormattedMessage id="actions" />,
          key: 'action',
          align: 'right',
          fixed: 'right',
          render: (_, i) => (
            <ButtonGroup selected={i.id === hoveredID} align="right">
              <ContributorButton
                icon={<EditFilled />}
                onClick={() => dispatch(openEditItemModal(i))}
              />
              <ConfirmDeleteModal<Item>
                item={i}
                deleteItem={i => deleteItem.mutate(i.id)}
                loading={deleteItem.isLoading}
              >
                <FormattedMessage
                  id="deleteItemPrompt"
                  values={{ name: i.name }}
                />
              </ConfirmDeleteModal>
            </ButtonGroup>
          ),
        },
      ]}
      onChange={onChangeHandler}
      scroll={{ y: '' }}
    />
  )
}

export default Items
